// Generated by scripts/build-rules-content.mjs from country-dk.json (aligned 9 Oct 2026).
// Content for sales: plain sentences, no em dash, no person or company names.
// Edit here or regenerate; tests/rules-explainer.test.mjs checks the writing rules.
import type { CountryGuide } from "../types";

export const GUIDE_DENMARK: CountryGuide = {
  id: "denmark",
  code: "dk",
  title: "Denmark (DISA Industries)",
  shortName: "Denmark",
  entities: ["dk"],
  areaRoles: ["dk"],
  live: true,
  cardLine:
    "Almost every customer is abroad, many inquiries come from agents, and freight is usually not on the quotation.",
  summary:
    "DISA in Denmark sells spare parts almost only abroad: just three customers are Danish. Many inquiries come from agents who write for an end customer. Prices come from the customer's price list or price group, and a line below the margin limit needs a manager's approval. Freight is not on the quotation, except for customers who pay in advance. Norican I2Q runs in shadow mode: it prepares a draft, and sales always checks, decides and sends.",
  howInquiriesArrive: [
    "Customers and agents write to the shared mailbox Disa.Parts.DK. Mail that customers send to a personal address is redirected to the shared mailbox by a rule on the mail server, so the real sender is kept. Norican I2Q reads a copy of this mailbox and picks up a new mail within a few minutes. Nothing is moved, answered or sent. Five sales colleagues share the mailbox, each one owning a group of countries. Most inquiries are in English, some in the customer's own language. I2Q writes the reply draft in Danish for Danish customers and in German for German customers; every other customer gets an English draft, also customers in Norway and Sweden who write in their own language (decision of 11 October 2026).",
  ],
  notYet: [
    "Delivery dates: I2Q does not run CTP and does not have the F&O lead times yet, so a line usually has no delivery date. Run CTP in F&O as today.",
    "The reply draft is prepared on request, not for every new inquiry: open the case and ask for it.",
    "No owner routing: a case is not assigned to the colleague who owns the customer's country group. Take it yourself or assign it by hand.",
    "The end customer behind an agent is a suggestion from earlier documents, never certain. Check the name in the mail before you confirm it.",
  ],
  specialities: [
    {
      id: "dk-export-customers",
      title: "Almost every customer is abroad",
      status: "active",
      tone: "info",
      topic: "customer",
      checks:
        "Which customer account the inquiry belongs to. In Denmark this is nearly always a foreign account, grouped in F&O as Agent, EU, Intercompany, Local or Overseas.",
      why: "Only three DISA customers are Danish, so country, currency and terms come from a foreign account almost every time.",
      youSee:
        'Info. The customer card shows the account with its reason, for example "Known sender" or "Earlier documents of the domain". A suggestion shows "Suggested, please confirm".',
      whatToDo:
        'Read the reason under the name (Why). If it fits, click "Confirm" with the name on it. If not, click "Not them" or "Find customer" and search by name or account number.',
      example:
        "A foundry in Turkey asks for 20 wear plates. I2Q suggests the Turkish account because colleagues at the same domain were quoted on it before. You confirm it, and the prices of that account appear.",
      registryIds: [],
      glossaryIds: ["customer-finding"],
      source:
        'goa-knowledge.md, Part A section 3 Denmark; Part B section 2 DK (DK-rev [00:10], [00:23]); help topic "customer"',
    },
    {
      id: "dk-contacts-without-mail",
      title: "Few contacts with an e-mail address",
      status: "active",
      tone: "info",
      topic: "customer",
      checks: "Whether the sender is a known contact person on a customer account.",
      why: "Only 24 DK contacts have an e-mail address, because Denmark kept e-mail addresses on the account, so in Go Autonomous the sender often matched no account.",
      youSee:
        'Info. I2Q relies more on earlier quotations and orders of the sender, the domain or the agent. More cases show "Suggested, please confirm" or "Customer: not identified" than in other countries.',
      whatToDo:
        "Confirm or pick the customer yourself. Each confirmation helps the next inquiry from the same sender.",
      example:
        "A new buyer at a known Polish customer writes for the first time. No contact matches, but other people at the same domain were quoted before, so I2Q suggests that account.",
      registryIds: [],
      glossaryIds: ["customer-finding"],
      source: "goa-knowledge.md, Part A section 2.1 (sender to account) and section 3 Denmark",
    },
    {
      id: "dk-agents",
      title: "Agents: the earlier quotations decide which party comes first",
      status: "active",
      tone: "info",
      topic: "customer",
      checks:
        "Whether the sender is an agent writing on behalf of an end customer. DK agents cover Brazil, Slovakia and the Czech Republic, Poland, the former Yugoslavia, Portugal, Spain, Bulgaria, Turkey, Korea and Taiwan.",
      why: "One agent writes for many customers, and Go Autonomous could link an agent to only one customer, so it put quotes on the wrong account.",
      youSee:
        'Info. The customer card shows both parties and marks one with "Quoted first" (product owner, 14 October 2026). An account of the agent for this end customer comes first. Else the earlier quotations of the sender decide: a trading house that sales quoted on its own account comes first. Else the end customer comes first. This is always a suggestion.',
      whatToDo:
        'Check that the party with "Quoted first" is right for this mail. Make the quotation on that account and send it to the agent.',
      example:
        "An agent in Asia asks for a price for a foundry it represents. I2Q puts the foundry first and shows the agent next to it. You confirm the foundry, quote on that account and reply to the agent.",
      registryIds: [],
      glossaryIds: ["agents-and-distributors"],
      source:
        "goa-knowledge.md, Part A section 2.1 (Agents; Issue Log #116; DK agents msg); Part B section 2 DK (DK-rev [00:24]-[00:27])",
    },
    {
      id: "dk-agent-list",
      title: "A fixed list of agent mail domains",
      status: "planned",
      tone: "info",
      topic: "customer",
      checks:
        "Whether the sender's mail domain is on a list of known DK agents, so I2Q always treats them as agents and never as the customer.",
      why: "In Go Autonomous an agent was sometimes taken as the customer itself, which put the quote on the wrong account.",
      youSee:
        "Planned. Today I2Q recognises an agent from earlier documents only, not from a list.",
      whatToDo:
        'Until then, check the sender type on the customer card. If an agent shows as "Customer", pick the end customer by hand.',
      example:
        "An agent in Bulgaria writes for the first time from a new domain. Without the list, I2Q may not see that it is an agent. You pick the end customer.",
      registryIds: [],
      glossaryIds: ["agents-and-distributors"],
      source:
        "goa-knowledge.md, Part A section 4 Intake (Agents); section 8 backlog M9; Part B section 5 M3",
    },
    {
      id: "dk-spanish-agent-invoice",
      title: "Exception: one Spanish agent is also the invoice account",
      status: "planned",
      tone: "info",
      topic: "customer",
      checks:
        "Whether the inquiry comes from the one Spanish agent that Denmark handles differently from all other agents.",
      why: "For this agent the invoice goes to the agent itself, not to the end customer, and delivery goes to its warehouse or straight to the end customer.",
      youSee:
        "Planned. I2Q has no special rule for this agent yet and treats it like any other agent.",
      whatToDo:
        "Change the invoice account to the agent yourself and choose the delivery address: its warehouse or the end customer.",
      example:
        "A Spanish agent asks for a price for a foundry near Bilbao. You quote on the end customer's account, set the agent as invoice account and deliver to its warehouse.",
      registryIds: [],
      glossaryIds: ["agents-and-distributors"],
      renamedFrom: "dk-euro-equip",
      source: "goa-knowledge.md, Part B section 2 DK (DK-rev [00:24]-[00:27]; OW1 [02:15]-[02:16])",
    },
    {
      id: "dk-price-list",
      title: "Prices come from the customer's price list",
      status: "active",
      tone: "info",
      topic: "price",
      checks:
        "Which price applies: first the customer's own price list in F&O, otherwise the price group of the account, valid on the day and for the quantity asked. DK price groups are EU-100, EU-105 (France) and ROW-100 (rest of the world).",
      why: "Go Autonomous used only these F&O prices, and quotes stayed correct where the prices were kept up to date.",
      youSee:
        "Info. Once the customer is certain or confirmed, each line shows the list price. A price that is no longer valid shows as expired.",
      whatToDo:
        "Check the price. A discount you give by hand is not known to I2Q; add it yourself in F&O.",
      example:
        "A French customer in price group EU-105 asks for 4 filter cartridges. I2Q fills in the EU-105 price valid today for a quantity of 4.",
      registryIds: ["pricing.no_price"],
      glossaryIds: ["never-zero-price"],
      modes: {
        "pricing.no_price": {
          dk: "block",
        },
      },
      source:
        "goa-knowledge.md, Part A section 2.3 (Price source) and section 3 Denmark (GoA sales quotations - DK.xlsx)",
    },
    {
      id: "dk-missing-price",
      title: "No price: ask pricing through the price status code",
      status: "active",
      tone: "blocked",
      topic: "price",
      checks: "Whether an item has a valid price for this customer.",
      why: "In Denmark a missing price was a top reason why quotes stopped, and a zero price must never reach a customer.",
      youSee:
        'Blocked: the line shows "No price", and the prepared quotation says "No price was found for ...". The other lines still show. Automatic routing of the price request is planned.',
      whatToDo:
        'Set the price status code on the item in F&O to "quote request" with your initials and a comment. Pricing moves it on to "ready". Filter on "ready" plus your initials, then finish the quotation.',
      example:
        'A customer in Brazil asks for a rare sand mixer part with no price. You set "quote request" with your initials. Two days later the item shows "ready" and you complete the quote.',
      registryIds: ["pricing.no_price"],
      glossaryIds: ["never-zero-price"],
      modes: {
        "pricing.no_price": {
          dk: "block",
        },
      },
      source:
        "goa-knowledge.md, Part A section 2.3 (No price / zero price); Part B section 2 DK (DK-rev [00:18]-[00:22]); Part B section 5 M6",
    },
    {
      id: "dk-margin",
      title: "Margin check",
      status: "active",
      tone: "blocked",
      topic: "price",
      checks:
        "Where F&O has a cost for the item, how much of the price is left after cost (the contribution ratio). It is compared with the margin limit for Denmark.",
      why: "Go Autonomous stopped quotes with too little profit so that a person looks at them first, and this rule is kept on purpose.",
      youSee:
        'Blocked until a manager approves: the line shows "Margin: needs approval". If F&O has no cost for the item, the line shows "Margin: not checked (no cost in F&O)", and the prepared quotation is held as well. Only colleagues with margin rights see the figures. Everyone else sees the result only.',
      whatToDo:
        'Check the price. If it is right, ask your manager to release it with "Approve price". If the item, price, quantity or customer changes later, the approval must be given again. You can also change the price in F&O before you send.',
      example:
        'A pin for a moulding machine comes out below the margin limit. The line shows "Margin: needs approval". Your manager approves it because the customer has a framework deal.',
      registryIds: ["pricing.margin"],
      glossaryIds: ["contribution-ratio"],
      modes: {
        "pricing.margin": {
          dk: "block",
        },
      },
      source:
        'backend pricing/thresholds.py (CODE_TABLE dk 0.40); goa-knowledge.md, Part A section 2.3 (Contribution ratio gate); help topic "case-page"',
    },
    {
      id: "dk-freight",
      title: "No freight on the quotation",
      status: "active",
      tone: "info",
      topic: "freight",
      checks:
        'Whether a freight line belongs on the quotation. Denmark is in the "no freight on the quote" model: freight is added later, at invoicing.',
      why: "Freight rules differ in every country, and missing or wrong freight was the most named problem with Go Autonomous.",
      youSee:
        'Info. The preview\'s freight says "Does not apply", with "This entity adds no freight at quote time." A note says "Payment terms are not in master data, so a customer who pays in advance is not recognised."',
      whatToDo:
        "Nothing for normal customers. For customers who pay in advance, see the next card.",
      example:
        "An Italian customer asks for 10 nozzles. The draft has the 10 nozzles and no freight line, as usual for DISA.",
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          dk: "warn",
        },
      },
      source:
        "goa-knowledge.md, Part A section 9.3 (freight as one rule per entity, three models); Part A section 2.5 (Freight elsewhere)",
    },
    {
      id: "dk-prepayment",
      title: "Customers who pay in advance need a pro-forma with freight",
      status: "active",
      tone: "info",
      topic: "freight",
      checks:
        "Whether the customer's payment terms ask for payment before delivery, for example 30A70N10, 50A50N30, ADVANCE or a letter of credit (L/C). This is common for customers in Brazil and for some in Poland, Hungary and Bulgaria.",
      why: "These customers pay before we ship, so freight and insurance must be priced up front on a pro-forma invoice.",
      youSee:
        'Info on every Denmark case: "Payment terms are not in master data, so a customer who pays in advance is not recognised." I2Q cannot spot these customers yet, because it does not have their payment terms.',
      whatToDo:
        "Add freight by hand when the customer pays in advance. Make the quotation and a pro-forma invoice, with freight and insurance from the DK freight calculator.",
      example:
        "A Brazilian foundry with terms ADVANCE asks for 6 swing frames weighing 180 kg. You add freight and insurance to Brazil from the calculator and send quotation plus pro-forma.",
      plannedPart:
        'Recognise customers who pay in advance from their payment terms, and mark them "handle by hand".',
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          dk: "warn",
        },
      },
      source:
        "goa-knowledge.md, Part A section 2.5 (Payment terms; Freight elsewhere) and section 3 Denmark; Part B section 2 DK (DK-rev [00:07]-[00:08], [00:25]-[00:26])",
    },
    {
      id: "dk-mode-of-delivery",
      title: "Air by default, suggest sea for heavy parts",
      status: "planned",
      tone: "info",
      topic: "mail-and-case",
      checks: "The mode of delivery on the quotation.",
      why: "DK ships by air as the default, but for heavy parts sea freight is much cheaper and sales offer it before the customer asks.",
      youSee: "Planned. I2Q does not fill in or check the mode of delivery yet.",
      whatToDo: "For heavy items, offer sea freight as an option in your reply.",
      example:
        "A customer asks for wear plates weighing 2 tonnes. You quote air as standard and offer sea freight as a cheaper option.",
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md, Part B section 2 DK (OW1 [01:03]-[01:07])",
    },
    {
      id: "dk-delivery-address",
      title: "Many accounts have only a business address",
      status: "planned",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Which delivery address goes on the quotation: a delivery address on the account, otherwise the business address, unless the mail names another one.",
      why: "Many DK accounts hold only a business address, and Go Autonomous kept asking for a delivery address it could never find.",
      youSee:
        "Planned. I2Q does not set the delivery address yet, and it will not ask again and again for it.",
      whatToDo:
        "If the mail names a delivery address, use it. Otherwise use the business address of the account.",
      example:
        "A Hungarian customer's account has only its head office address. The mail asks for delivery to the plant in Győr. You put the Győr address on the quotation.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md, Part A section 3 Denmark; Part B section 2 DK (Ticket [00:18]-[00:21]); Part B section 5 M15",
    },
    {
      id: "dk-item-status",
      title: "Blocked and phased-out items",
      status: "active",
      tone: "blocked",
      topic: "items",
      checks:
        "The item status code of each matched item. Only items with status OK can be quoted without a second look.",
      why: "Go Autonomous quoted phased-out items and failed without a message on new or revised items.",
      youSee:
        'Blocked: "Blocked for sale in F&O" on the line. Warning: "Phase-out item", with the replacement number when F&O has one. Warning for a code that needs a check: "Line 2 has signal code REV, which needs a check before quoting."',
      whatToDo:
        "Check the item in F&O. Quote the successor if there is one, or ask the product team.",
      example:
        "A customer asks for an old nozzle. The line shows that the item is phased out. You check F&O and quote the new nozzle instead.",
      registryIds: ["gate.signal_review"],
      glossaryIds: ["signal-codes"],
      modes: {
        "gate.signal_review": {
          dk: "warn",
        },
      },
      source:
        "goa-knowledge.md, Part A section 2.2 (Signal codes); backend pricing/lookup.py (BLOCKED_SIGNALS, PHASE_OUT_SIGNALS)",
    },
    {
      id: "dk-generic-items",
      title: "Generic items need the machine variant",
      status: "active",
      tone: "warning",
      topic: "items",
      checks:
        "Whether a matched item has the signal code GENERIC in F&O, which means it stands for several machine variants.",
      why: "A generic item stands for several machine variants, and the right part depends on the customer's machine.",
      youSee:
        'Warning: "Line 2 is a generic item without a machine variant." It is a warning, not a block, because sales sometimes quote the generic item as it is.',
      whatToDo:
        "Ask the customer for the machine variant and quote that item. Quote the generic item only when you know it fits.",
      example:
        "A customer asks for a filter element that is generic in F&O. You ask for the machine number and quote the variant for that machine.",
      registryIds: ["gate.signal_generic"],
      glossaryIds: ["signal-codes"],
      modes: {
        "gate.signal_generic": {
          dk: "warn",
        },
      },
      source:
        "rules/registry.py gate.signal_generic; modes.yaml dk warn (decision D12, 30-day measurement 9 Oct 2026); rules/data/signal_codes.csv GENERIC",
    },
    {
      id: "dk-oem-and-sub-groups",
      title: "OEM and SUB item groups are outside parts sales",
      status: "active",
      tone: "warning",
      topic: "items",
      checks: "Whether a line is in an item group starting with OEM or SUB.",
      why: "These groups are not part of Denmark's parts sales, so the Denmark item group mapping gives them no sales pool.",
      youSee:
        'Warning: "Line 2 is in item group ..., which is outside parts sales." The line does not count when I2Q proposes the sales pool.',
      whatToDo: "Check with your manager whether Denmark quotes this line.",
      example:
        "An inquiry has 5 spare parts and 1 line in an OEM group. The OEM line gets the warning, and the pool follows the spare parts.",
      registryIds: ["gate.out_of_scope_group"],
      glossaryIds: ["out-of-scope-item-groups"],
      modes: {
        "gate.out_of_scope_group": {
          dk: "warn",
        },
      },
      source:
        "rules/data/manual_item_groups.csv (dk OEM, dk SUB out_of_scope); rules/header.py (DK OEM/SUB unmapped)",
    },
    {
      id: "dk-replacement-number",
      title: "Show the old number next to the successor",
      status: "active",
      tone: "warning",
      topic: "items",
      checks:
        "When an item has been replaced, the quotation shows the successor and the number the customer asked for.",
      why: "DK customers order by their old number and asked that the quotation names it, so they see why the number changed.",
      youSee:
        'Warning: "Line 3: use 654321, it replaces 123456." When F&O marks the old item as phased out, the line also shows "Phase-out item, replacement". The quotation itself does not print both numbers yet.',
      whatToDo: 'Write "replaces <old number>" in the line text or in your reply.',
      example:
        'The customer asks for part 123456. It has been replaced by 654321. You quote 654321 and write "replaces 123456".',
      plannedPart: "Both numbers on the quotation.",
      registryIds: ["gate.successor"],
      glossaryIds: ["successor-items"],
      modes: {
        "gate.successor": {
          dk: "warn",
        },
      },
      source:
        "goa-knowledge.md, Part A section 2.2 (Alternatives and successors); section 6.2 (what users asked for)",
    },
    {
      id: "dk-lead-time",
      title: "Delivery dates come from F&O (CTP)",
      status: "active",
      tone: "warning",
      topic: "price",
      checks:
        "The delivery date per line. DK is the only country that calculates it with CTP (capable to promise) in F&O. I2Q warns when the F&O lead time is over 99 days or the line ships after the requested date.",
      why: "CTP gives a real date, but it takes 2 to 10 minutes per quotation and is not always stable.",
      youSee:
        'Warning: "Line 2 has a lead time of 120 days." I2Q does not run CTP. I2Q does not have the F&O lead times yet, so you rarely see these warnings today. The check for a price without a lead time is off.',
      whatToDo: "Run CTP in F&O as today and put the date on the quotation.",
      example:
        "A customer needs a part by 15 November. CTP gives 2 December. You tell the customer the date in your reply.",
      plannedPart: "Lead times from F&O, so these warnings show reliably.",
      registryIds: ["pricing.lead_time", "pricing.after_requested", "pricing.ship_today"],
      glossaryIds: ["lead-time-warnings"],
      modes: {
        "pricing.lead_time": {
          dk: "warn",
        },
        "pricing.after_requested": {
          dk: "warn",
        },
        "pricing.ship_today": {
          dk: "off",
        },
      },
      source:
        'goa-knowledge.md, Part B section 2 DK (DK-rev [00:00]-[00:05], [00:14]-[00:15]); Part A section 2.3 (Lead time); help topic "not-yet"',
    },
    {
      id: "dk-language",
      title: "Reply language and quotation language",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        "The language of the reply draft and of the printed quotation. Danish customers get a Danish draft, German customers a German draft, every other customer an English draft, also customers in Norway and Sweden (decision of 11 October 2026).",
      why: "The DK desk answers its export customers in English; Danish and German customers expect their own language, and a reply in the wrong language looks careless.",
      youSee:
        "Info. The reply draft is in Danish, German or English by the customer's language, not in the language of the inquiry. The quotation preview shows the document language: the customer's, else English (US), marked \"(company default)\".",
      whatToDo:
        "Check the reply language and change it in the draft when the customer needs another one. In F&O, make sure the customer's language is set so the quotation prints in it.",
      example:
        "A Spanish customer writes in Spanish. The reply draft is in English, and the quotation prints in Spanish because the account has language es. A Norwegian customer writes in Norwegian: the draft is in English.",
      registryIds: [],
      glossaryIds: ["translation"],
      source: "goa-knowledge.md, Part A section 2.5 (Quote document language; Reply language)",
    },
    {
      id: "dk-header",
      title: "Quotation header: what is filled in from where",
      status: "active",
      tone: "warning",
      topic: "quotation",
      checks:
        "The fields at the top of the quotation. Sales pool AMO and the sales fields come from the item group with the most lines. Sales unit, payment terms and delivery terms come from the account. Customer reference: the customer's inquiry number. Requisition: empty.",
      why: 'In Go Autonomous a wrong or outdated sales unit (an old "TT" unit) made every DK test quote fail.',
      youSee:
        "The quotation preview shows the proposed sales pool, the sales fields and the customer reference. Warning when no pool can be proposed. A note lists the customer fields that are not available yet, such as sales unit, payment terms and delivery terms.",
      whatToDo:
        "Fill in these fields in F&O as today. If the sales unit points to someone who has left, tell your manager.",
      example:
        "A customer in Taiwan asks via the agent. The header gets sales pool AMO, the Taiwan sales unit with the agent as account manager, currency EUR and the agent's inquiry number as reference.",
      plannedPart: "Sales unit, payment terms and delivery terms from the customer account.",
      registryIds: ["header.pool", "header.not_available"],
      glossaryIds: ["quotation-header", "customer-reference-fields"],
      modes: {
        "header.pool": {
          dk: "warn",
        },
        "header.not_available": {
          dk: "info",
        },
      },
      source:
        "goa-knowledge.md, Part A section 2.4 (Sales pool; Sales unit), section 3 Denmark (Sales Units DK v3.xlsx), section 5 (header fields); Part B section 2 DK (Ticket [00:11]-[00:21])",
    },
    {
      id: "dk-validity",
      title: "How long the quotation is valid",
      status: "planned",
      tone: "info",
      topic: "quotation",
      checks: "The expiry date of the quotation and the validity in the reply text.",
      why: "DK quotations in F&O expire after about 90 days, while the standard reply texts say 30 days, so the two can contradict each other.",
      youSee: "Planned. I2Q will use one validity for both.",
      whatToDo: "Make the validity in your reply match the expiry date in F&O.",
      example:
        'The F&O quotation expires on 7 January. Your reply says "valid until 7 January", not "valid 30 days".',
      registryIds: [],
      glossaryIds: ["quotation-validity"],
      source: "goa-knowledge.md, Part A section 2.5 (Validity / expiry); section 8 backlog S3",
    },
    {
      id: "dk-owner",
      title: "Who handles the inquiry: owner by country group",
      status: "planned",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Which of the five DK sales colleagues gets the case, based on the customer's country.",
      why: 'A fully shared queue without clear ownership did not work for DK users, who asked "what is for me?"',
      youSee:
        "Planned. Today a case can be assigned by hand in the queue; automatic assignment by country group is not built.",
      whatToDo: "Assign the case to yourself or to the colleague who owns that country group.",
      example: "An inquiry from Poland arrives. The colleague who owns Eastern Europe takes it.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md, Part A section 2.4 (Draft owner), section 6.3 (adoption); section 8 backlog S5",
    },
    {
      id: "dk-blocked-senders",
      title: "Competitors, sanctioned countries and portals",
      status: "active",
      tone: "blocked",
      topic: "customer",
      checks:
        "Whether the sender, or the original sender behind a forward, is a competitor, sits in a sanctioned country or comes from a portal used for quotations. For Denmark the only listed portal is one car maker's supplier portal.",
      why: "DK sells to many countries, and Go Autonomous sent these mails to manual handling so no one quoted by mistake.",
      youSee:
        'Blocked, all three. For example "The sender\'s domain is on the competitor list." The case is held and the queue shows this reason first.',
      whatToDo:
        "Do not quote a sanctioned country; pass it to your manager for an export control check. Quote a competitor only if your manager agrees. Answer a portal in the portal.",
      example:
        "A colleague forwards an inquiry from a .ru domain. The case is held with the sanctions reason, and you tell your manager.",
      registryIds: ["gate.competitor", "gate.sanctioned", "gate.portal"],
      glossaryIds: ["competitor-domains", "sanctioned-countries", "portal-customers"],
      modes: {
        "gate.competitor": {
          dk: "block",
        },
        "gate.sanctioned": {
          dk: "block",
        },
        "gate.portal": {
          dk: "block",
        },
      },
      source:
        "goa-knowledge.md, Part A section 2.1 (Competitor domains; Sanctioned countries; Portal customers)",
    },
    {
      id: "dk-large-and-credit",
      title: "Large quotations and customers on credit block",
      status: "active",
      tone: "warning",
      topic: "price",
      checks:
        "Whether a quotation is above 50,000 EUR, and whether the customer's account is on credit block.",
      why: "Above 50,000 EUR Go Autonomous handed over to a person, and a credit block stops the order later, so it is better to know it at quote time.",
      youSee:
        'Warning above 50,000 EUR: "The quotation total of 63,000.00 EUR is above the approval limit of 50,000.00 EUR." In a currency without a limit you see a note instead. The credit block is not shown yet.',
      whatToDo:
        "Ask your manager to approve a quotation above the limit before it goes out. If the account is blocked, tell the customer that payment must be cleared before an order.",
      example:
        "A quotation for 12 lines comes to 63,000 EUR. The case shows the approval warning, and you get the usual approval before sending.",
      plannedPart: "A warning when the account is on credit block.",
      registryIds: ["gate.doa_limit"],
      glossaryIds: ["large-quotation", "prospect-and-credit-block"],
      modes: {
        "gate.doa_limit": {
          dk: "warn",
        },
      },
      source:
        "goa-knowledge.md, Part A section 2.1 (Credit block) and section 2.3 (Delegation of authority)",
    },
    {
      id: "dk-pitfalls",
      title: "Typical pitfalls from Go Autonomous in Denmark",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks: "Not a check: what went wrong before, so you know what to look for.",
      why: "DK testers lost weeks in Go Autonomous because of a look-alike test button, an outdated sales unit, a delivery address prompt that kept coming back and lost line order.",
      youSee:
        "Info. In I2Q the shadow mode badge is on every page, lines keep the order of the inquiry, and every proposal shows its reason.",
      whatToDo:
        'Compare the line order and the item numbers with the inquiry before you send. Report anything odd with "Give feedback".',
      example:
        "A 14-line inquiry: you check that line 9 in the draft is still line 9 in the customer's list before you build the quotation in F&O.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md, Part B section 2 DK (Ticket [00:03], [00:11]-[00:21], [00:26]-[00:34]); Part A section 5 (line order)",
    },
  ],
};
